import { IExcelDownloadDefinition } from "@lib/ShiOneClient";
import { Workbook, Worksheet } from "exceljs";
import { DateTime } from "luxon";

export interface IExcelDownloadDefinitionWithFormat extends IExcelDownloadDefinition {
  numFmt?: string;
}

const MaxColumnWidth = 32;
const MinColumnWidth = 15;

function populateHeaders(
  excelDownloadDefinitions: IExcelDownloadDefinitionWithFormat[],
): Workbook {
  const wb = new Workbook();
  const ws = wb.addWorksheet("Data");
  let colIndex = 1;
  for (const definition of excelDownloadDefinitions) {
    ws.getCell(1, colIndex++).value = definition.header;
  }
  return wb;
}

function populateRow(
  ws: Worksheet,
  sourceItem: any,
  downloadDefinitions: IExcelDownloadDefinitionWithFormat[],
  rowIndex: number,
) {
  let colIndex = 1;
  for (const definition of downloadDefinitions) {
    if (!definition.dataField) continue;
    const propertyInfo = sourceItem[definition.dataField];
    if (propertyInfo === undefined) {
      ws.getCell(rowIndex, colIndex++).value = "";
      continue;
    }

    const value = propertyInfo;
    switch (typeof value) {
      case "number":
        ws.getCell(rowIndex, colIndex++).value = value;
        ws.getCell(rowIndex, colIndex - 1).numFmt = definition.numFmt ?? "0";
        break;
      case "string":
        ws.getCell(rowIndex, colIndex++).value = value;
        break;
      case "object":
        if (value instanceof Date) {
          ws.getCell(rowIndex, colIndex++).value =
            DateTime.fromJSDate(value).toISODate();
        } else ws.getCell(rowIndex, colIndex++).value = "";
        break;
      default:
        ws.getCell(rowIndex, colIndex++).value = value?.toString();
        break;
    }
  }
}

function autoSizeColumns(
  ws: Worksheet,
  downloadDefinitions: IExcelDownloadDefinitionWithFormat[],
) {
  for (let i = 1; i <= downloadDefinitions.length; i++) {
    let maxCellLength = MinColumnWidth;
    ws.eachRow((row) => {
      const cell = row.getCell(i);
      const length = cell.value?.toString().length || 0;
      maxCellLength = Math.max(length, maxCellLength);
    });
    const column = ws.getColumn(i);
    column.width = Math.min(maxCellLength, MaxColumnWidth);
  }
}

function populateExcelFile(
  data: any,
  excelDownloadDefinitions: IExcelDownloadDefinitionWithFormat[],
): Workbook {
  const downloadDefinitions = excelDownloadDefinitions;
  const wb = populateHeaders(downloadDefinitions);
  const ws = wb.getWorksheet("Data") as Worksheet;

  let rowIndex = 2;
  for (const item of data) {
    populateRow(ws, item, downloadDefinitions, rowIndex);
    rowIndex++;
  }

  autoSizeColumns(ws, downloadDefinitions);

  return wb;
}

export async function generateAndDownloadExcel(
  data: any[],
  downloadDefinitions: IExcelDownloadDefinitionWithFormat[],
  filename: string,
): Promise<void> {
  const wb = populateExcelFile(data, downloadDefinitions);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const link = document.createElement("a");
  const url = window.URL.createObjectURL(blob);
  link.href = url;
  link.setAttribute("download", `${filename}.xlsx`);
  link.click();
  window.URL.revokeObjectURL(url);
  link.remove();
}
